<div>
  <div class="container-fluid">
    <!-- Page Header -->
    <div class="row mb-4">
      <div class="col">
        <button class="btn btn-link text-muted px-0 mb-2" (click)="goBack()">
          <i class="bi bi-arrow-left me-1"></i>
          Back to Safety Tests
        </button>
        <div class="d-flex justify-content-between align-items-start">
          <div>
            <h1 class="h2 mb-1 fw-bold" *ngIf="test">{{ test.title }}</h1>
            <h1 class="h2 mb-1 fw-bold" *ngIf="!test && loading">Loading...</h1>
            <p class="text-muted mb-0" *ngIf="test?.shortName">{{ test?.shortName }}</p>
          </div>
          <div class="d-flex gap-2" *ngIf="test">
            <button class="btn btn-outline-secondary" (click)="editTest()">
              <i class="bi bi-pencil me-2"></i>Edit
            </button>
            <button *ngIf="canActivate()" class="btn btn-success" (click)="openActivateModal()">
              <i class="bi bi-check-circle me-2"></i>Activate
            </button>
            <button *ngIf="canCreateNewVersion()" class="btn btn-outline-primary" (click)="openNewVersionModal()">
              <i class="bi bi-plus-circle me-2"></i>New Version
            </button>
            <button *ngIf="canDelete()" class="btn btn-outline-danger" (click)="openDeleteModal()">
              <i class="bi bi-trash me-2"></i>Delete
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Error Message -->
    <div *ngIf="errorMessage" class="alert alert-danger" role="alert">
      <i class="bi bi-exclamation-triangle-fill me-2"></i>
      {{ errorMessage }}
    </div>

    <!-- Loading State -->
    <div *ngIf="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <!-- Test Details -->
    <div *ngIf="!loading && test" class="row g-4">
      <!-- Main Content -->
      <div class="col-lg-8">
        <!-- Test Info Card -->
        <div class="card mb-4">
          <div class="card-header">
            <h5 class="mb-0"><i class="bi bi-info-circle me-2"></i>Test Information</h5>
          </div>
          <div class="card-body">
            <div class="row g-3">
              <div class="col-md-6">
                <label class="form-label text-muted small">Status</label>
                <div>
                  <span class="badge" [ngClass]="getStatusClass(test.status)">
                    {{ getStatusLabel(test.status) }}
                  </span>
                </div>
              </div>
              <div class="col-md-6">
                <label class="form-label text-muted small">Category</label>
                <div>{{ category?.name || '—' }}</div>
              </div>
              <div class="col-md-6">
                <label class="form-label text-muted small">Passing Score</label>
                <div>{{ test.passingScore }}%</div>
              </div>
              <div class="col-md-6">
                <label class="form-label text-muted small">Time Limit</label>
                <div>{{ test.timeLimit ? test.timeLimit + ' minutes' : 'No limit' }}</div>
              </div>
              <div class="col-md-6">
                <label class="form-label text-muted small">Version</label>
                <div>v{{ test.version }}</div>
              </div>
              <div class="col-md-6">
                <label class="form-label text-muted small">Questions</label>
                <div>{{ test.questions.length }}</div>
              </div>
              <div class="col-12" *ngIf="test.description">
                <label class="form-label text-muted small">Description</label>
                <div>{{ test.description }}</div>
              </div>
              <div class="col-md-6">
                <label class="form-label text-muted small">Shuffle Questions</label>
                <div>{{ test.shuffleQuestions ? 'Yes' : 'No' }}</div>
              </div>
              <div class="col-md-6">
                <label class="form-label text-muted small">Shuffle Options</label>
                <div>{{ test.shuffleOptions ? 'Yes' : 'No' }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Study Guide -->
        <div class="card mb-4" *ngIf="test.studyGuideUrl">
          <div class="card-header">
            <h5 class="mb-0"><i class="bi bi-file-earmark-pdf me-2"></i>Study Guide</h5>
          </div>
          <div class="card-body">
            <div class="d-flex align-items-center gap-3">
              <i class="bi bi-file-earmark-pdf text-danger fs-2"></i>
              <div>
                <div class="fw-medium">{{ test.studyGuideFilename || 'Study Guide' }}</div>
                <a [href]="test.studyGuideUrl" target="_blank" class="btn btn-sm btn-outline-primary mt-1">
                  <i class="bi bi-download me-1"></i>Download
                </a>
              </div>
            </div>
          </div>
        </div>

        <!-- Questions Preview -->
        <div class="card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h5 class="mb-0"><i class="bi bi-list-ol me-2"></i>Questions ({{ test.questions.length }})</h5>
            <div class="d-flex gap-2">
              <div class="btn-group btn-group-sm" *ngIf="test.questions && test.questions.length > 0">
                <button class="btn btn-outline-secondary" (click)="expandAllQuestions()">
                  <i class="bi bi-chevron-expand"></i> Expand All
                </button>
                <button class="btn btn-outline-secondary" (click)="collapseAllQuestions()">
                  <i class="bi bi-chevron-contract"></i> Collapse All
                </button>
              </div>
              <div class="dropdown" *ngIf="test.questions && test.questions.length > 0">
                <button class="btn btn-sm btn-outline-primary dropdown-toggle" type="button" data-bs-toggle="dropdown">
                  <i class="bi bi-download me-1"></i>Export
                </button>
                <ul class="dropdown-menu dropdown-menu-end">
                  <li><a class="dropdown-item" href="javascript:void(0)" (click)="exportQuestions()">
                    <i class="bi bi-file-earmark-text me-2"></i>Export Questions (CSV)
                  </a></li>
                  <li><a class="dropdown-item" href="javascript:void(0)" (click)="exportResults()">
                    <i class="bi bi-graph-up me-2"></i>Export Results (CSV)
                  </a></li>
                </ul>
              </div>
            </div>
          </div>
          <div class="card-body p-0">
            <div *ngIf="!test.questions || test.questions.length === 0" class="text-center text-muted py-4">
              No questions added yet
            </div>
            <div class="accordion accordion-flush" *ngIf="test.questions && test.questions.length > 0">
              <div class="accordion-item" *ngFor="let question of test.questions; let i = index">
                <h2 class="accordion-header">
                  <button class="accordion-button"
                          [class.collapsed]="!isQuestionExpanded(question.id)"
                          type="button"
                          (click)="toggleQuestion(question.id)">
                    <div class="d-flex align-items-center gap-3 w-100 me-3 overflow-hidden">
                      <span class="badge bg-light text-dark">{{ i + 1 }}</span>
                      <span class="badge bg-secondary">{{ getQuestionTypeBadge(question.type) }}</span>
                      <span class="text-truncate">{{ question.text }}</span>
                    </div>
                  </button>
                </h2>
                <div class="accordion-collapse collapse" [class.show]="isQuestionExpanded(question.id)">
                  <div class="accordion-body">
                    <div class="d-flex align-items-center gap-2 mb-3">
                      <span class="badge bg-primary">Q{{ i + 1 }}</span>
                      <span class="badge bg-secondary">{{ getQuestionTypeBadge(question.type) }}</span>
                    </div>
                    <p class="mb-3">{{ question.text }}</p>
                    <div class="mb-3">
                      <div *ngFor="let option of question.options; let j = index"
                           class="d-flex align-items-center gap-2 py-2 px-3 mb-1 rounded border"
                           [class.bg-success-subtle]="option.isCorrect"
                           [class.border-success-subtle]="option.isCorrect"
                           [class.bg-body-secondary]="!option.isCorrect">
                        <span class="badge" [class.bg-success]="option.isCorrect" [class.bg-secondary]="!option.isCorrect">
                          {{ ['A', 'B', 'C', 'D', 'E', 'F'][j] }}
                        </span>
                        <span>{{ option.text }}</span>
                        <i *ngIf="option.isCorrect" class="bi bi-check-circle-fill text-success ms-auto"></i>
                      </div>
                    </div>
                    <div *ngIf="question.explanation" class="alert alert-info py-2 px-3 mb-0">
                      <small><strong>Explanation:</strong> {{ question.explanation }}</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Sidebar -->
      <div class="col-lg-4">
        <!-- Metadata Card -->
        <div class="card mb-4">
          <div class="card-header">
            <h6 class="mb-0"><i class="bi bi-calendar me-2"></i>Metadata</h6>
          </div>
          <div class="card-body">
            <div class="mb-3">
              <label class="form-label text-muted small mb-0">Created</label>
              <div>{{ formatDateTime(test.createdAt) }}</div>
            </div>
            <div class="mb-3">
              <label class="form-label text-muted small mb-0">Last Updated</label>
              <div>{{ formatDateTime(test.updatedAt) }}</div>
            </div>
            <div *ngIf="test.effectiveDate">
              <label class="form-label text-muted small mb-0">Effective Date</label>
              <div>{{ formatDate(test.effectiveDate) }}</div>
            </div>
          </div>
        </div>

        <!-- Version History Card -->
        <div class="card">
          <div class="card-header d-flex justify-content-between align-items-center cursor-pointer"
               (click)="toggleVersionHistory()">
            <h6 class="mb-0">
              <i class="bi bi-clock-history me-2"></i>Version History
            </h6>
            <i class="bi" [class.bi-chevron-down]="!showVersionHistory" [class.bi-chevron-up]="showVersionHistory"></i>
          </div>
          <div class="collapse" [class.show]="showVersionHistory">
            <div class="card-body">
              <div *ngIf="loadingVersions" class="text-center py-3">
                <div class="spinner-border spinner-border-sm text-primary" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
              </div>
              <div *ngIf="!loadingVersions && versionHistory.length === 0" class="text-muted text-center py-3">
                No version history available
              </div>
              <div *ngIf="!loadingVersions && versionHistory.length > 0">
                <div *ngFor="let version of versionHistory"
                     class="d-flex gap-3 py-2 border-bottom"
                     [class.border-bottom-0]="version === versionHistory[versionHistory.length - 1]">
                  <div>
                    <span class="badge" [ngClass]="getStatusClass(version.status)">v{{ version.version }}</span>
                  </div>
                  <div class="flex-grow-1">
                    <div class="small text-muted">{{ formatDate(version.createdAt) }}</div>
                    <div *ngIf="version.versionNotes" class="small">{{ version.versionNotes }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Delete Confirmation Modal -->
<div class="modal fade" id="deleteModal" tabindex="-1">
  <div class="modal-dialog">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title">Delete Test</h5>
        <button type="button" class="btn-close" aria-label="Close" data-bs-dismiss="modal"></button>
      </div>
      <div class="modal-body">
        <p>Are you sure you want to delete this test?</p>
        <p class="text-danger mb-0"><strong>{{ test?.title }}</strong></p>
        <p class="text-muted small mt-2">This action cannot be undone. Only draft tests can be deleted.</p>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">Cancel</button>
        <button type="button" class="btn btn-danger" (click)="deleteTest()" [disabled]="deleting">
          <span *ngIf="deleting" class="spinner-border spinner-border-sm me-1"></span>
          Delete Test
        </button>
      </div>
    </div>
  </div>
</div>

<!-- Activate Confirmation Modal -->
<div class="modal fade" id="activateModal" tabindex="-1">
  <div class="modal-dialog">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title">Activate Test</h5>
        <button type="button" class="btn-close" aria-label="Close" data-bs-dismiss="modal"></button>
      </div>
      <div class="modal-body">
        <p>Are you sure you want to activate this test?</p>
        <p class="fw-medium mb-2">{{ test?.title }}</p>
        <div class="alert alert-info py-2">
          <small>
            <i class="bi bi-info-circle me-1"></i>
            Once activated, structural changes (passing score, time limit) cannot be modified.
            You'll need to create a new version for major changes.
          </small>
        </div>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">Cancel</button>
        <button type="button" class="btn btn-success" (click)="activateTest()" [disabled]="activating">
          <span *ngIf="activating" class="spinner-border spinner-border-sm me-1"></span>
          Activate Test
        </button>
      </div>
    </div>
  </div>
</div>

<!-- New Version Modal -->
<div class="modal fade" id="newVersionModal" tabindex="-1">
  <div class="modal-dialog">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title">Create New Version</h5>
        <button type="button" class="btn-close" aria-label="Close" data-bs-dismiss="modal"></button>
      </div>
      <div class="modal-body">
        <p>Create a new version of this test? The current version will be retired when the new version is activated.</p>
        <div class="mb-3">
          <label class="form-label">Version Notes (optional)</label>
          <textarea class="form-control" rows="3" [(ngModel)]="newVersionNotes"
                    placeholder="Describe what's changing in this version..."></textarea>
        </div>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">Cancel</button>
        <button type="button" class="btn btn-primary" (click)="createNewVersion()" [disabled]="creatingVersion">
          <span *ngIf="creatingVersion" class="spinner-border spinner-border-sm me-1"></span>
          Create New Version
        </button>
      </div>
    </div>
  </div>
</div>
